import type { Fx } from '@wyn/game-core';
import type { ReactNode } from 'react';
import { TONE_TEXT } from '../lib/theme.ts';

/** The flourish that pops over the stage ("Clean cut!"). Keyed by tick so a repeat replays the animation. */
export function FxBanner({ fx }: { fx: Fx | null }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-2 z-10 flex justify-center" aria-live="polite">
      {fx && (
        <span key={`${fx.tick}-${fx.text}`} className={`fx-pop smallcaps text-xl drop-shadow ${TONE_TEXT[fx.tone]}`}>
          {fx.text}
        </span>
      )}
    </div>
  );
}

export type Zone = { from: number; to: number; className: string };

/**
 * A horizontal gauge, all in percent: shaded zones (drawn in order, so the tightest goes last), hatched no-go
 * zones, and a needle. `band` is shorthand for one target zone; `danger` hatches from there to the end.
 * `reading` reports the underlying value to assistive tech when it isn't the needle's percent.
 */
export function Track({ value, label, band, bandClass = 'bg-verdigris/35', zones = [], danger, hatch = [], reading }: {
  value: number;
  label: string;
  band?: [number, number];
  bandClass?: string;
  zones?: Zone[];
  danger?: number;
  hatch?: [number, number][];
  reading?: { now: number; min: number; max: number };
}) {
  const all = band ? [{ from: band[0], to: band[1], className: bandClass }, ...zones] : zones;
  const no = danger !== undefined ? [...hatch, [danger, 100] as [number, number]] : hatch;
  const r = reading ?? { now: value, min: 0, max: 100 };
  return (
    <div className="well relative h-7" role="meter" aria-label={label} aria-valuenow={Math.round(r.now)} aria-valuemin={r.min} aria-valuemax={r.max}>
      {all.map((z, i) => <div key={i} className={`absolute inset-y-0 ${z.className}`} style={{ left: `${z.from}%`, width: `${z.to - z.from}%` }} />)}
      {no.map(([from, to], i) => <div key={`h${i}`} className="hatch absolute inset-y-0" style={{ left: `${from}%`, width: `${to - from}%` }} />)}
      <div className="needle" style={{ left: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

export function Progress({ value, label }: { value: number; label: string }) {
  return (
    <div className="space-y-1">
      <div className="h-2 bg-rule" role="progressbar" aria-label={label} aria-valuenow={Math.round(value * 100)}>
        <div className="h-full bg-gilt" style={{ width: `${Math.min(100, value * 100)}%` }} />
      </div>
      <div className="text-xs text-ink-faint">{label}</div>
    </div>
  );
}

/** The one big button a real-time game is played with. Space works too. */
export function HoldButton({ children, held, ...props }: { children: ReactNode; held: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`w-full select-none touch-none border-2 py-4 text-lg transition-colors ${held ? 'border-gilt bg-gilt text-vellum' : 'border-gilt/70 text-gilt'}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Stage({ fx, children }: { fx: Fx | null; children: ReactNode }) {
  return <div className="relative space-y-3">{<FxBanner fx={fx} />}{children}</div>;
}
